import axios from 'axios';
import { FC, useEffect, useState, useMemo } from 'react';
import { useLazyQuery } from '@apollo/client';
import { Input, Select, Layout, FormItem, Button } from 'Components';
import { Formik, useFormikContext } from 'formik';
import { GET_GROUP, GET_GROUPS_LIST } from 'Services/Api/Groups/Queries';
import { GetGroupResponse, GetGroupsListResponse, GetGroupsListRequest } from 'Services/Api/Groups/Types';
import {
	GROUP_ACTIVE_STATUS_LIST,
	GROUP_ACTIVE_STATUS_LABEL_MAP,
	HIERARCHY_LABEL_TENANT_VALUES,
	HIERARCHY_LABEL_GROUP_VALUES,
	getAllowedChildLabels,
	isLeafLabel
} from 'app-config';
import { ArchivedField, GroupField, AutoactivateField, SsoField } from 'Pages/Group/AddGroup/components';
import PropertyField from './PropertyField';
import { createInitialValuesDerivedFromParent } from '../../EditGroup.helpers';
import { useTranslation } from 'react-i18next';
import { EditGroupValues } from 'Pages/Group/Group.types';
import { getHierarchyLabel } from 'Services/Api/HierarchyLabel/HierarchyLabelService';
interface DetailsProps {
	isTenant: boolean;
	groupId: string;
	hierarchyLabelSource?: string;
}

const Details: FC<DetailsProps> = ({ isTenant, groupId, hierarchyLabelSource }) => {
	const { values, setFieldValue } = useFormikContext<EditGroupValues>();
	const { t } = useTranslation();
	const [hasChildren, setHasChildren] = useState(false);
	const [parentHierarchyLabel, setParentHierarchyLabel] = useState<string | undefined>(undefined);
	const [parentLabelLoading, setParentLabelLoading] = useState<boolean>(!isTenant && !!values.parent?.id);
	const [
		getParentGroup,
		parentGroupResponse
	] = useLazyQuery<GetGroupResponse>(GET_GROUP);
	const [getChildGroups, childGroupsResult] = useLazyQuery<
		GetGroupsListResponse,
		GetGroupsListRequest
	>(GET_GROUPS_LIST, { fetchPolicy: 'no-cache' });

	// Check if this group has children (for Individual validation)
	useEffect(() => {
		if (groupId) {
			getChildGroups({
				variables: {
					sort: 'name',
					filters: { depth: 1, parentId: Number(groupId) }
				}
			});
		}
	}, [groupId, getChildGroups]);

	useEffect(() => {
		const children = childGroupsResult.data?.listGroups;
		if (children && children.length > 0) {
			setHasChildren(true);
		}
	}, [childGroupsResult.data]);

	// ── Fetch parent's hierarchy label to constrain the dropdown ──
	useEffect(() => {
		const parentId = values.parent?.id;
		if (parentId) {
			setParentLabelLoading(true);
			getHierarchyLabel(parentId as number)
				.then((res) => {
					if (res.success && res.data?.node_type) {
						setParentHierarchyLabel(res.data.node_type);
					}
				})
				.catch(() => {
					// On failure, allow all labels
				})
				.finally(() => {
					setParentLabelLoading(false);
				});
		}
	}, [values.parent?.id]);

	/** Hierarchy label options filtered by the parent's label. */
	const allowedLabels = useMemo(() => {
		if (isTenant) return HIERARCHY_LABEL_TENANT_VALUES;
		return getAllowedChildLabels(parentHierarchyLabel);
	}, [isTenant, parentHierarchyLabel]);

	/** True when the parent is a leaf node (Individual). */
	const isParentLeaf = !isTenant && isLeafLabel(parentHierarchyLabel);

	useEffect(() => {
		if (parentGroupResponse.data?.getGroup) {
			const {
				tenant,
				parent,
				canBePropertyToAncestors
			} = createInitialValuesDerivedFromParent(
				parentGroupResponse.data.getGroup
			);
			const newValues: Pick<
				EditGroupValues,
				'tenant' | 'parent' | 'parentId' | 'canBePropertyToAncestors'
			> = {
				tenant,
				parent,
				parentId: parent?.id ?? null,
				canBePropertyToAncestors
			};
			setFieldValue('tenant', newValues.tenant);
			setFieldValue('parent', newValues.parent);
			setFieldValue('parentId', newValues.parentId);
			setFieldValue(
				'canBePropertyToAncestors',
				newValues.canBePropertyToAncestors
			);
		}
	}, [parentGroupResponse.data?.getGroup, setFieldValue]);

	const onParentGroupChange = (groupId: string) => {
		getParentGroup({
			variables: {
				id: groupId
			}
		});
	};

	const title = isTenant
		? t('group.editGroup.sections.tenantDetails')
		: t('general');

	interface ApiCreds{
		[key: string]:any
	}

	function getAPIcreds(){
		const currentAPIcred: ApiCreds = {};
		const urlhost = window.location.host;
		let APIurl = 'https://api-stage.venzagrc.com';
		if(!urlhost.includes('ev.') && !urlhost.includes('tg.')){
			APIurl = 'https://api-prod.venzagrc.com';
		}
		let i=1;
		let xx=0;
		let yy=0;
		currentAPIcred.userdata = {};
		currentAPIcred.newapitoken = {};
		for(i = 0; i < localStorage.length; i++){
			if(localStorage.key(i)?.includes('.userDat')){
				currentAPIcred.userdata[xx] = localStorage.getItem(localStorage.key(i)!);
				xx++;
			}
		}
		for(i = 0; i < localStorage.length; i++){
			if(localStorage.key(i)?.includes('.accessToke')){
				currentAPIcred.newapitoken[yy] = localStorage.getItem(localStorage.key(i)!);
				yy++;
			}
		}
		const fullAPIurl = APIurl+'/?awsToken='+currentAPIcred.newapitoken[0];
		return fullAPIurl;
	}
	const runSSOreport = async () => {
		try {
			const response = await axios({
				method: 'post',
				headers: { 
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				url: getAPIcreds(),
				data: {
					callfor: 'ssourlreport',
					urls: values.domains,
					tenant: groupId,
				},
			});
			//console.log('Success:', values.domains+'...'+groupId+'...'+response.data);
			const blob = new Blob([response.data], { type: 'text/csv' });
			const url = URL.createObjectURL(blob);
			const a = document.createElement('a');
			//const report = window.open(url, "_blank");
			a.href = url;
			a.download = 'SSO-URL-Report-Group-'+groupId.toString();
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
    		URL.revokeObjectURL(url);
		} catch (error) {
			console.log('API ERROR: '+error);
		}
	};

	return (
		<Layout.List fullHeight>
			<Layout.List.ListItem fullHeight>
				<Layout.List.Section title={title}>
					<Input.FormikField
						name="name"
						label={t(
							isTenant ? 'group.tenantName' : 'group.groupName'
						)}
						required
					/>
					{isTenant && (
						<Input.FormikField
							name="tenantId"
							label={t('group.tenantID')}
							required
						/>
					)}

					{values.parent && (
						<FormItem label={t('group.parentGroup')} required>
							<GroupField
								disabledValue={{
									key: Number(groupId),
									title: values.name
								}}
								initialValue={[
									{
										key: values.parent.id,
										title: values.parent.name
									}
								]}
								onSubmit={(value) => {
									onParentGroupChange(value.key as string);
								}}
							/>
						</FormItem>
					)}
					{!isTenant && (
						<FormItem label={t('group.tenant')} required>
							<Select
								value={{
									key: values.tenant?.id,
									value: values.tenant?.id,
									label: values.tenant?.name
								}}
								labelInValue
								disabled
							/>
						</FormItem>
					)}

					<Input.FormikField
						name="syncCode"
						label={t('group.syncCode')}
					/>
					<div style={{ display: 'none' }}>
						<PropertyField />
					</div>
				<Select.FormikField
					name="hierarchyLabel"
					label={t('Type')}
					required
					disabled={isTenant || parentLabelLoading}
					onChange={(value: string) => {
						// When hierarchy label is set to "Property", automatically set isProperty to true
						const isProperty = value === 'Property';
						setFieldValue('isProperty', isProperty);
					}}
				>
				{allowedLabels.map((label) => (
					<Select.Option key={label} value={label}>
						{label}
					</Select.Option>
				))}
				</Select.FormikField>
				{isTenant && (
					<div style={{ fontSize: 12, color: '#595959', background: '#f5f5f5', border: '1px solid #d9d9d9', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
						<strong>Organization</strong> — Tenant represents the top-level management company or organization.<br />
						<strong>Property</strong> — Tenant represents a single hotel or location (no separate property layer required).<br />
						<strong>Brand / Region</strong> — Tenant root represents a brand or regional grouping.
					</div>
				)}
				{!isTenant && parentLabelLoading && (
					<div style={{ color: '#1890ff', fontSize: 12, marginTop: -8, marginBottom: 12 }}>
						Loading parent hierarchy label…
					</div>
				)}
					{isParentLeaf && (
						<div style={{ color: '#d4380d', background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
							The parent node is labeled "Individual". The current label options are restricted accordingly.
						</div>
					)}
					{values.hierarchyLabel === 'Individual' && hasChildren && (
						<div style={{ color: '#d4380d', background: '#fff2e8', border: '1px solid #ffbb96', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
							This node has child groups. Nodes labeled "Individual" must not have children. Please remove child nodes or choose a different label before saving.
						</div>
					)}
					{hasChildren && values.hierarchyLabel !== 'Individual' && values.hierarchyLabel === 'Sub-Group' && (
						<div style={{ color: '#ad6800', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
							Note: This node has children. Verify that "Sub-Group" is the correct label for a node with child groups.
						</div>
					)}
					{hierarchyLabelSource === 'default' && values.hierarchyLabel === 'Individual' && hasChildren && (
						<div style={{ color: '#ad6800', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
							Legacy node: This node was auto-assigned "Individual" but has child groups. Please review and assign the correct hierarchy label.
						</div>
					)}
					{hierarchyLabelSource === 'default' && values.hierarchyLabel !== 'Individual' && (
						<div style={{ color: '#ad6800', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: 4, padding: '8px 12px', marginTop: -8, marginBottom: 12 }}>
							This node's label was auto-assigned by the system. Please verify and confirm the hierarchy label is correct.
						</div>
					)}
				</Layout.List.Section>
				<Layout.List.Section title={t('group.status')}>
					<Select.FormikField
						name="active"
						label={t('group.status')}
						required
					>
						{GROUP_ACTIVE_STATUS_LIST.map((status) => (
							<Select.Option key={status} value={status}>
								{GROUP_ACTIVE_STATUS_LABEL_MAP[status]()}
							</Select.Option>
						))}
					</Select.FormikField>
					<ArchivedField />
					<AutoactivateField />
					{isTenant && values.autoactivate && (
						<SsoField />
					)}
					{isTenant && (
						<div>
						Corporate Domains:<br />
						(ex: venzagroup.com use commas to separate multiple domains)
						</div>
					)}
					{isTenant && (
						<Input.FormikField
						name="domains"
						/>
					)}
					{isTenant && (
						<Button
							shape="round"
							type="primary"
							onClick={runSSOreport}
						>
							{t('Run SSO Domain Report')}
						</Button>
					)}
				</Layout.List.Section>
			</Layout.List.ListItem>
		</Layout.List>
	);
};

export default Details;
